import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, tCategory, tEnv, tDirection } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { CritBadge, StatusBadge } from "@/components/shared/Badges";
import RelationDialog from "@/components/shared/RelationDialog";
import AlternativeManager from "@/components/shared/AlternativeManager";
import FailureDomainAssignDialog from "@/components/shared/FailureDomainAssignDialog";
import AssetVulnerabilities from "@/components/shared/AssetVulnerabilities";
import ControlsPanel from "@/components/shared/ControlsPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Server, Boxes, Building2, ArrowRight, ArrowDownLeft, ArrowUpRight,
  Wifi, WifiOff, Loader2, Activity, Info, Plus, Pencil, Trash2, Eye, GitBranch, Layers, X,
} from "lucide-react";

const TYPE_ICON = { asset: Server, service: Boxes, organization: Building2 };

function Field({ label, children, mono }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</dt>
      <dd className={`mt-1 text-sm ${mono ? "font-mono" : ""}`}>{children ?? <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid, action }) {
  return (
    <section className="border border-border bg-card rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <h2 className="flex items-center gap-2 font-head font-semibold">{Icon && <Icon className="h-4 w-4 text-primary" />} {title}</h2>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function RelTable({ rows, emptyLabel, onNavigate, writable, onView, onEdit, onDelete, onAlt }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  return (
    <div className="border border-border rounded-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-border">
            <TableHead>Actif / Service</TableHead>
            <TableHead>Type de relation</TableHead>
            <TableHead>Criticité</TableHead>
            <TableHead>Direction</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const Icon = TYPE_ICON[r.node.type] || Server;
            const navigable = r.node.type === "asset" || r.node.type === "service";
            const to = r.node.type === "service" ? `/services/${r.node.id}` : `/assets/${r.node.id}`;
            return (
              <TableRow key={r.edge_id} className="border-border" data-testid={`rel-row-${r.edge_id}`}>
                <TableCell className="font-medium">
                  <button className={`flex items-center gap-2 ${navigable ? "hover:text-primary" : ""}`} onClick={() => navigable && onNavigate(to)}>
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" /> {r.node.name}
                    {navigable && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                  </button>
                </TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm font-mono text-xs">{r.type_label}</Badge></TableCell>
                <TableCell><CritBadge value={r.criticality} /></TableCell>
                <TableCell className="text-muted-foreground text-xs">{tDirection(r.direction)}</TableCell>
                <TableCell>{r.node.status ? <StatusBadge value={r.node.status} /> : "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Alternatives" onClick={() => onAlt(r.edge_id)} data-testid={`alt-rel-${r.edge_id}`}><GitBranch className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onView(r.edge_id)} data-testid={`view-rel-${r.edge_id}`}><Eye className="h-4 w-4" /></Button>
                    {writable && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(r.edge_id)} data-testid={`edit-rel-${r.edge_id}`}><Pencil className="h-4 w-4" /></Button>}
                    {writable && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => onDelete(r.edge_id)} data-testid={`delete-rel-${r.edge_id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState({ open: false, mode: "create", edgeId: null });
  const [altMgr, setAltMgr] = useState({ open: false, edgeId: null, label: "" });
  const [fdAssign, setFdAssign] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["asset-detail", id],
    queryFn: async () => (await api.get(`/assets/${id}/detail`)).data,
  });
  const { data: graph } = useQuery({
    queryKey: ["graph"],
    queryFn: async () => (await api.get("/graph")).data,
  });

  const del = useMutation({
    mutationFn: async (edgeId) => api.delete(`/dependencies/${edgeId}`),
    onSuccess: () => { toast.success("Relation supprimée"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const dissocFd = useMutation({
    mutationFn: async (domainId) => api.post("/failure-domains/dissociate", { asset_id: id, domain_id: domainId }),
    onSuccess: () => { toast.success("Association retirée (le domaine est conservé)"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["asset-detail", id] });
    qc.invalidateQueries({ queryKey: ["graph"] });
  };
  const onDelete = (edgeId) => { if (window.confirm("Supprimer définitivement cette relation ?")) del.mutate(edgeId); };

  if (isLoading)
    return <AppShell title="Fiche actif"><div className="flex items-center gap-2 text-muted-foreground font-mono text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div></AppShell>;
  if (isError || !data)
    return <AppShell title="Fiche actif"><p className="text-sm text-muted-foreground">Actif introuvable.</p><Button variant="outline" className="mt-4 rounded-sm gap-2" onClick={() => navigate("/assets")}><ArrowLeft className="h-4 w-4" /> Retour</Button></AppShell>;

  const a = data.asset;
  const at = a.attributes || {};
  const writable = can("dependency:write");
  const options = (graph?.nodes || []).filter((n) => n.id !== id).map((n) => ({ id: n.id, name: n.name, type: n.type }));

  return (
    <AppShell title={a.name} subtitle="Fiche actif détaillée — NODE + RELATIONSHIP"
      actions={<Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => navigate("/assets")} data-testid="back-to-assets"><ArrowLeft className="h-4 w-4" /> Actifs</Button>}>
      <div className="space-y-6" data-testid="asset-detail">
        <Section title="Identité" icon={Info} testid="section-identity">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Field label="Identifiant" mono>{a.id}</Field>
            <Field label="Nom">{a.name}</Field>
            <Field label="Type" mono>{a.type}</Field>
            <Field label="Environnement">{tEnv(at.environment)}</Field>
            <Field label="Criticité"><CritBadge value={at.criticality} /></Field>
            <Field label="Statut"><StatusBadge value={at.status} /></Field>
            <Field label="Catégorie">{tCategory(at.category)}</Field>
            <div className="col-span-2 md:col-span-4"><Field label="Description">{at.description || null}</Field></div>
          </dl>
        </Section>

        <Section title="Informations techniques" icon={Server} testid="section-technical">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Field label="Technologie">{at.technology || null}</Field>
            <Field label="Version" mono>{at.version || null}</Field>
            <Field label="Adresse IP (fictive)" mono>{at.ip_address || null}</Field>
            <Field label="Exposition Internet">
              {at.internet_exposed
                ? <span className="inline-flex items-center gap-1.5 text-orange-400 text-sm"><Wifi className="h-3.5 w-3.5" /> Exposé</span>
                : <span className="inline-flex items-center gap-1.5 text-green-400 text-sm"><WifiOff className="h-3.5 w-3.5" /> Non exposé</span>}
            </Field>
            <Field label="Localisation logique">{at.logical_location || null}</Field>
          </dl>
        </Section>

        <Section title="Gouvernance" icon={Building2} testid="section-governance">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Field label="Responsable">{at.owner || null}</Field>
            <Field label="Organisation">{user?.org_name}</Field>
            <Field label="Date de création">{a.created_at ? new Date(a.created_at).toLocaleString("fr-FR") : null}</Field>
            <Field label="Dernière évaluation">{at.last_assessment ? new Date(at.last_assessment).toLocaleDateString("fr-FR") : <span className="text-muted-foreground">Non évalué</span>}</Field>
          </dl>
        </Section>

        <Section title={`Domaines de défaillance — ${(data.failure_domains || []).length}`} icon={Layers} testid="section-failure-domains"
          action={writable && <Button size="sm" className="rounded-sm gap-2" onClick={() => setFdAssign(true)} data-testid="assign-fd-button"><Plus className="h-4 w-4" /> Associer un domaine</Button>}>
          <p className="text-xs text-muted-foreground mb-4">Périmètres communs pouvant affecter cet actif simultanément avec d'autres. Un actif peut appartenir à plusieurs domaines.</p>
          {(data.failure_domains || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun domaine de défaillance associé.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {data.failure_domains.map((d) => (
                <span key={d.id} className="inline-flex items-center gap-2 pl-3 pr-1.5 py-1 rounded-sm border border-border bg-secondary/40 text-sm" data-testid={`fd-chip-${d.id}`}>
                  <Layers className="h-3.5 w-3.5 text-primary" />
                  <button className="hover:text-primary" onClick={() => navigate(`/failure-domains/${d.id}`)} data-testid={`fd-nav-${d.id}`}>{d.name}</button>
                  <Badge variant="outline" className="rounded-sm font-mono text-[10px]">{d.type_label}</Badge>
                  {writable && <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400" title="Retirer l'association" onClick={() => { if (window.confirm("Retirer cette association ? Le domaine n'est pas supprimé.")) dissocFd.mutate(d.id); }} data-testid={`fd-dissoc-${d.id}`}><X className="h-3.5 w-3.5" /></Button>}
                </span>
              ))}
            </div>
          )}
        </Section>

        <AssetVulnerabilities assetId={id} assetName={a.name} />

        <ControlsPanel endpoint={`/assets/${id}/controls`} queryKey={["asset-controls", id]} />

        <Section title={`Dépendances entrantes (amont) — ${data.incoming.length}`} icon={ArrowDownLeft} testid="section-incoming"
          action={writable && <Button size="sm" className="rounded-sm gap-2" onClick={() => setDialog({ open: true, mode: "create", edgeId: null })} data-testid="add-relation-button"><Plus className="h-4 w-4" /> Ajouter une relation</Button>}>
          <RelTable rows={data.incoming} writable={writable} emptyLabel="Aucune dépendance entrante."
            onNavigate={(to) => navigate(to)} onView={(eid) => setDialog({ open: true, mode: "edit", edgeId: eid })}
            onAlt={(eid) => setAltMgr({ open: true, edgeId: eid, label: `Vers ${a.name}` })}
            onEdit={(eid) => setDialog({ open: true, mode: "edit", edgeId: eid })} onDelete={onDelete} />
        </Section>
        <Section title={`Dépendances sortantes (aval) — ${data.outgoing.length}`} icon={ArrowUpRight} testid="section-outgoing">
          <RelTable rows={data.outgoing} writable={writable} emptyLabel="Aucune dépendance sortante."
            onNavigate={(to) => navigate(to)} onView={(eid) => setDialog({ open: true, mode: "edit", edgeId: eid })}
            onAlt={(eid) => setAltMgr({ open: true, edgeId: eid, label: `Depuis ${a.name}` })}
            onEdit={(eid) => setDialog({ open: true, mode: "edit", edgeId: eid })} onDelete={onDelete} />
        </Section>

        <Section title="Cycle de vie (NODE · RELATIONSHIP · STATE · EVENT)" icon={Activity} testid="section-lifecycle">
          <p className="text-xs text-muted-foreground mb-4">Modèle préparé : <span className="font-mono">Asset = NODE</span>, <span className="font-mono">Dependency = RELATIONSHIP</span>. STATE/EVENT structurés sans moteur en v0.1.</p>
          {data.events?.length > 0 ? (
            <ul className="space-y-2">
              {data.events.map((e, i) => (
                <li key={i} className="flex items-center gap-3 text-sm"><span className="h-1.5 w-1.5 rounded-full bg-primary" /><span className="font-mono text-xs">{e.event_type}</span><span className="text-muted-foreground text-xs">{new Date(e.occurred_at).toLocaleString("fr-FR")}</span></li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">Aucun évènement enregistré.</p>}
        </Section>
      </div>

      <RelationDialog
        open={dialog.open}
        onOpenChange={(v) => setDialog((d) => ({ ...d, open: v }))}
        mode={dialog.mode}
        edgeId={dialog.edgeId}
        fixedNode={{ id: a.id, name: a.name, role: "source" }}
        options={options}
        onSaved={refresh}
      />
      <AlternativeManager
        open={altMgr.open}
        onOpenChange={(v) => setAltMgr((m) => ({ ...m, open: v }))}
        primaryEdgeId={altMgr.edgeId}
        primaryLabel={altMgr.label}
        options={options}
        onChanged={refresh}
      />
      <FailureDomainAssignDialog
        open={fdAssign}
        onOpenChange={setFdAssign}
        assetId={id}
        current={data.failure_domains || []}
        onChanged={refresh}
      />
    </AppShell>
  );
}
